import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useEffect } from 'react'
import { FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { useRouteError } from 'react-router'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import CaleoSecondaryButton from 'Components/reusable/Buttons/CaleoSecondaryButton'
import caleoError from 'assets/images/Caleo-error.png'

export function setWithExpiry(key: string, value: string, ttl: number) {
  const item = {
    value: value,
    expiry: new Date().getTime() + ttl,
  }
  localStorage.setItem(key, JSON.stringify(item))
}

export function getWithExpiry(key: string) {
  const itemString = window.localStorage.getItem(key)
  if (!itemString) return null

  const item = JSON.parse(itemString)
  const isExpired = new Date().getTime() > item.expiry

  if (isExpired) {
    localStorage.removeItem(key)
    return null
  }

  return item.value
}

// Lazy-loaded chunks fail to load when a tab opened before a deploy asks for the old build's
// files, which no longer exist. Browsers word the error differently.
const chunkLoadErrorMessages = [
  'Failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite CSS preload
]

const CHUNK_FAILED_KEY = 'chunk_failed'
// Must outlast reactLazyWithRetry's retries (0.5 + 1 + 2 + 4 + 8 s), which run after the reload
// before the error reaches ErrorFallback. Shorter, and ErrorFallback reloads again in a loop.
const CHUNK_FAILED_TTL = 60000

export function isChunkLoadError(error: unknown) {
  const message = error instanceof Error ? error.message : ''
  return chunkLoadErrorMessages.some(chunkMessage => message.includes(chunkMessage))
}

/**
 * Reloads the page to get the current build, unless a reload was already done in the last
 * minute (then the new build is broken too, and reloading again would loop).
 * Returns true when the page is reloading.
 */
export function reloadOnceForChunkError() {
  if (getWithExpiry(CHUNK_FAILED_KEY)) return false

  setWithExpiry(CHUNK_FAILED_KEY, 'true', CHUNK_FAILED_TTL)
  window.location.reload()
  return true
}

/**
 * Error page shown when a page crashes. A chunk-load error first reloads the page once to get
 * the current build; the page stays only if that did not help.
 */
function ErrorPage({ error }: { error: unknown }) {
  const { t } = useTranslation()
  const chunkLoadError = isChunkLoadError(error)
  const message = error instanceof Error ? error.message : ''

  useEffect(() => {
    if (chunkLoadError) {
      console.error('chunk failed', error)
      reloadOnceForChunkError()
    }
  }, [chunkLoadError, error])

  return (
    <Box sx={{ textAlign: 'center', padding: 2 }}>
      <Typography component="h1" sx={{ color: 'primary.main', fontSize: '2.5rem', paddingTop: '2vw' }}>
        {t('error.header')}
      </Typography>
      <Typography sx={{ color: 'primary.main', fontSize: '1.25rem' }}>
        {t(chunkLoadError ? 'error.newVersion' : 'error.unexpected')}
      </Typography>
      <Box component="img" src={caleoError} alt="" sx={{ maxWidth: '45vw' }} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', marginBottom: 2 }}>
        <CaleoPrimaryButton label={t('error.reload')} clickAction={() => window.location.reload()} valid={true} />
        <CaleoSecondaryButton
          label={t('error.frontPage')}
          clickAction={() => window.location.assign('/')}
          valid={true}
        />
      </Box>
      {message && (
        <Box component="details" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          <summary>{t('error.details')}</summary>
          <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message}
          </Box>
        </Box>
      )}
    </Box>
  )
}

/** Fallback for react-error-boundary, used around the page content in Layout. */
export default function ErrorFallback({ error }: FallbackProps) {
  return <ErrorPage error={error} />
}

/** Router errorElement, for errors outside the page content (e.g. in Layout itself). */
export function RouteErrorFallback() {
  return <ErrorPage error={useRouteError()} />
}
